import { useNavegacao } from "@/lib/navegacao";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Copy, Check, Store, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMensagemOferta } from "@/hooks/use-ofertas";
import { linkWhatsAppCom, mascararTelefoneBR, telefoneWhatsappValido } from "@shared/mercado";

/**
 * "Enviar — pelo seu WhatsApp": o painel que aparece DEPOIS de publicar.
 *
 * ═══ POR QUE ELE VEM DEPOIS ═════════════════════════════════════════════════
 *
 * A escolha de destinatário era a PRIMEIRA coisa que o formulário pedia, antes
 * de existir o que enviar — e travava quem só tem o grupo da praça. Aqui ela é
 * execução, não decisão: a carga já está na praça, e isto é mandar o recado.
 *
 * ⚠️ O app NÃO dispara mensagem. Ele monta o texto, o produtor toca e o
 * WhatsApp DELE abre. Número novo de plataforma mandando oferta para
 * desconhecido é exatamente o padrão que o WhatsApp trata como maior risco de
 * bloqueio — e o grupo da praça é justamente onde a chamada tem de cair.
 *
 * ⚠️ O texto de COPIAR é o ANÔNIMO. Quem está no cadastro do produtor recebe um
 * link nominal (o servidor monta); a org da praça e o "outro número" recebem o
 * mesmo texto do grupo. Identidade vem do cadastro — ver a rota `/mensagem`.
 *
 * "Enviado" é marca LOCAL e otimista: serve para o produtor não se perder na
 * lista de doze compradores. Não vira estado no servidor — quem envia é ele, e
 * o app não tem como saber se a mensagem saiu de verdade.
 */
export function PainelChamada({ ofertaId }: { ofertaId: string }) {
  const { toast } = useToast();
  const { abrir } = useNavegacao();
  const { data, isLoading } = useMensagemOferta(ofertaId);
  const [enviados, setEnviados] = useState<string[]>([]);
  const [outroNumero, setOutroNumero] = useState("");

  async function copiar(texto: string, titulo: string, descricao: string) {
    try {
      await navigator.clipboard.writeText(texto);
      toast({ title: titulo, description: descricao });
    } catch (e: any) {
      // Nunca `catch {}`: sem permissão de clipboard o produtor precisa saber
      // que nada foi copiado — senão ele cola o que estava na área antes.
      toast({ title: "Não consegui copiar", description: e?.message, variant: "destructive" });
    }
  }

  function marcarEnviado(id: string) {
    setEnviados(prev => prev.indexOf(id) >= 0 ? prev : prev.concat([id]));
  }

  if (isLoading || !data) {
    return <div className="rounded-lg border bg-card h-40 animate-pulse" data-testid="painel-chamada-carregando" />;
  }

  const outroValido = telefoneWhatsappValido(outroNumero);

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="painel-chamada">
      <header className="border-b px-3 py-2">
        <p className="text-sm font-semibold">Enviar — pelo seu WhatsApp</p>
      </header>

      <div className="p-3 space-y-3">
        {data.destinatarios.length === 0 ? (
          /* Cadastro vazio e nenhuma org da praça com telefone: o card não fica
             vazio nem pede cadastro antes. Sobra o que sempre funcionou.

             ⚠️ A frase dizia "tem conta no Rizoma ainda", e virou mentira com o
             pré-cadastro (§B.4): esta lista é de quem tem TELEFONE para receber o
             link, com ou sem conta. Quem entra aqui não é quem não tem conta — é
             quem não tem número. */
          <p className="text-xs text-muted-foreground" data-testid="texto-sem-destinatarios">
            Nenhum comprador da praça tem WhatsApp cadastrado ainda — cole o texto no grupo.
          </p>
        ) : (
          <ul className="divide-y rounded-lg border" data-testid="lista-destinatarios">
            {data.destinatarios.map(d => (
              <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`destinatario-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.nome}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {d.telefoneMascarado ?? "sem telefone"}
                    {d.origem === "PRACA" && <> · na praça</>}
                  </p>
                </div>
                {d.url ? (
                  <span className="flex items-center gap-2 shrink-0">
                    {enviados.indexOf(d.id) >= 0 && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Check className="h-3 w-3" />enviado
                      </span>
                    )}
                    <Button variant="outline" size="sm" className="min-h-10" asChild>
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => marcarEnviado(d.id)}
                        data-testid={`button-enviar-${d.id}`}
                      >
                        <MessageCircle className="h-4 w-4 mr-1.5" />Enviar
                      </a>
                    </Button>
                  </span>
                ) : (
                  /* Sem telefone não há o que enviar — e o que falta é o
                     cadastro, não o comprador. Mesma ação da tela Compradores. */
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-10 shrink-0"
                    onClick={() => abrir(`/mercado/compradores/${d.compradorId}?editar=telefone`)}
                    data-testid={`button-adicionar-telefone-${d.id}`}
                  >
                    <Phone className="h-4 w-4 mr-1.5" />Adicionar telefone
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* O comprador sem cadastro — hoje, a maioria. Não force criar cadastro
            antes de mandar um WhatsApp: o cadastro nasce quando ele responder. */}
        <div className="space-y-1.5" data-testid="bloco-outro-numero">
          <Label htmlFor="outro-numero" className="text-xs uppercase text-muted-foreground">Outro número</Label>
          <div className="flex gap-2">
            <Input
              id="outro-numero"
              inputMode="tel"
              className="h-11"
              placeholder="(74) 99999-8888"
              value={mascararTelefoneBR(outroNumero)}
              onChange={e => setOutroNumero(e.target.value)}
              data-testid="input-outro-numero"
            />
            <Button
              variant="outline"
              className="h-11 shrink-0"
              disabled={!outroValido}
              asChild={outroValido}
              data-testid="button-enviar-outro-numero"
            >
              {outroValido ? (
                <a
                  href={linkWhatsAppCom(outroNumero, data.texto)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOutroNumero("")}
                >
                  <MessageCircle className="h-4 w-4 mr-1.5" />Enviar
                </a>
              ) : (
                <span><MessageCircle className="h-4 w-4 mr-1.5" />Enviar</span>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Vai o mesmo texto do grupo, sem o seu nome — ele aparece só para quem está no seu cadastro.
          </p>
        </div>

        <div className="border-t pt-3 space-y-2">
          {/* SEMPRE presente, com ou sem comprador cadastrado: é ele o
              mecanismo real de "toda a praça". */}
          <Button
            className="w-full min-h-11"
            onClick={() => copiar(data.texto, "Chamada copiada!", "Cole no grupo da praça; quem tiver interesse responde pelo link.")}
            data-testid="button-copiar-chamada"
          >
            <Copy className="h-4 w-4 mr-2" />Copiar texto para o grupo da praça
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => copiar(data.url, "Link copiado!", "É o mesmo link da chamada.")}
            data-testid="button-copiar-link"
          >
            <Copy className="h-4 w-4 mr-2" />Copiar só o link
          </Button>
        </div>

        <div className="rounded-lg rounded-tl-sm bg-sucesso-suave text-foreground text-[13px] leading-relaxed p-3 whitespace-pre-wrap break-words border border-sucesso/20" data-testid="previa-chamada">
          {data.texto}
        </div>

        <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
          <Store className="h-3.5 w-3.5 shrink-0 mt-px" />
          Quem responder aparece em Ofertas, e você decide para quem abrir os detalhes.
        </p>
      </div>
    </section>
  );
}
